import { useMemo } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { TopBar } from '@/components/layouts/TopBar'
import { ClientStickyHeader } from '@/components/features/client/ClientStickyHeader'
import { ClientProjectsByJobSite } from '@/components/features/client/ClientProjectsByJobSite'
import { Skeleton } from '@/components/ui/skeleton'
import { useClientDetailsV2 } from '@/hooks/useClientDetailsV2'
import { useClientProjectsByJobSiteV2 } from '@/hooks/useClientProjectsByJobSiteV2'
import { ApiError } from '@/lib/api/client'

export default function ClientDetailPage() {
  const { customerId } = useParams<{ customerId: string }>()

  if (!customerId) return <Navigate to="/home" replace />

  // Keyed by id: after a merge the page navigates from the deleted client to the kept one, and the
  // hooks would otherwise show the old client's data until the new fetch lands.
  return <ClientDetailPageInner key={customerId} customerId={customerId} />
}

function ClientDetailPageInner({ customerId }: { customerId: string }) {
  const {
    data: client,
    isLoading: clientLoading,
    error: clientError,
  } = useClientDetailsV2(customerId)

  const {
    data: projects,
    isLoading: projectsLoading,
    error: projectsError,
  } = useClientProjectsByJobSiteV2(customerId)

  const groups = useMemo(() => {
    return (projects?.groups || []).filter(Boolean)
  }, [projects])

  // SOLY-CUST-001 — a merged (deleted) or unknown customer answers 404: a clean empty state, never
  // the generic error screen nor an empty project list underneath.
  if (clientError instanceof ApiError && clientError.status === 404) {
    return (
      <div className="h-screen flex flex-col bg-background">
        <TopBar />
        <main className="flex-1 overflow-y-auto bg-muted/40">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:px-12">
            <div className="rounded-lg border bg-card p-6 text-sm" data-testid="client-not-found">
              <p className="text-foreground">Ce client n'existe plus. Il a peut-être été fusionné dans une autre fiche.</p>
              <Link to="/home" className="mt-3 inline-block text-primary underline underline-offset-4" data-testid="client-not-found-home-link">
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <TopBar />
      <main className="flex-1 overflow-y-auto bg-muted/40">
        {clientLoading ? (
          <div className="border-b border-border bg-background py-3">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </div>
          </div>
        ) : client ? (
          <ClientStickyHeader client={client} />
        ) : clientError ? (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:px-12">
            <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
              Erreur lors du chargement du client
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:px-12">
            <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">Client introuvable</div>
          </div>
        )}

        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-8 lg:px-12">
          {projectsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-6 w-56" />
              <Skeleton className="h-4 w-72" />
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            </div>
          ) : projectsError ? (
            <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
              Erreur lors du chargement des projets
            </div>
          ) : groups.length === 0 ? (
            <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">Aucun chantier/projet</div>
          ) : (
            <ClientProjectsByJobSite
              groups={groups}
              customerId={customerId}
              hasBillingAddress={!!client?.billingAddress?.details?.street}
            />
          )}
        </div>
      </main>
    </div>
  )
}
